import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '@/environments/environment';
import {deleteToken, getToken} from 'firebase/messaging';
import {messaging} from '@/app/config/firebase.config';
import {ToastService} from '@services/toast.service';
import {TranslateService} from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class PushNotificationService {

	constructor(
		private readonly http: HttpClient,
		private readonly toastService: ToastService,
		private readonly translate: TranslateService,
	) {}

	private readonly SERVICE_URI = environment.notificationsSubscriptionUrl;

	async subscribeToNotifications() {
		if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
			const message = this.translate.instant('notification.not-supported');
			this.toastService.show(message, "error");
			return false;
		}

		try {
			const permission = await Notification.requestPermission();

			if (permission !== 'granted') {
				const message = this.translate.instant('notification.permission-denied');
				this.toastService.show(message, "error");
				return false;
			}

			await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
				scope: '/',
				updateViaCache: 'none'
			});

			const registration = await navigator.serviceWorker.ready;
			await registration.update();

			const fcmToken = await getToken(messaging, {
				vapidKey: environment.vapidPublicKey,
				serviceWorkerRegistration: registration
			});

			const subscriptionData = {
				endpoint: `https://fcm.googleapis.com/fcm/send/${fcmToken}`,
				keys: {
					auth: '',
					p256dh: ''
				},
				fcmToken: fcmToken
			};

			this.http.post(`${this.SERVICE_URI}/subscribe`, subscriptionData, {
				responseType: 'text'
			})
				.subscribe({
					next: () => {
						const message = this.translate.instant('notification.subscribed');
						this.toastService.show(message, "success");
					},
					error: err => {
						console.error('❌ Erreur lors de l\'abonnement:', err);
						const message = this.translate.instant('notification.subscription-error');
						this.toastService.show(message, "error");
					}
				});
			return true;
		} catch (err) {
			console.error('❌ Erreur lors de l\'activation des notifications FCM:', err);

			if (err instanceof Error) {
				if (err.message.includes('messaging/permission-blocked')) {
					console.log('Permission de notification bloquée par l\'utilisateur.');
				} else if (err.message.includes('messaging/failed-service-worker-registration')) {
					console.log('Erreur d\'enregistrement du Service Worker.');
				}
			}
			return false;
		}
	}

	async getCurrentToken(): Promise<string | null> {
		try {
			const registration = await navigator.serviceWorker.ready;
			const token = await getToken(messaging, {
				vapidKey: environment.vapidPublicKey,
				serviceWorkerRegistration: registration
			});
			return token;
		} catch (error) {
			console.error('❌ Erreur récupération token:', error);
			return null;
		}
	}

	deleteSubscription(token: string) {
		this.http.delete(`${this.SERVICE_URI}/unsubscribe/${token}`).subscribe({
			next: () => {
				const message = this.translate.instant('notification.unsubscribed');
				this.toastService.show(message, "success");
			},
			error: err => {
				console.error('❌ Erreur lors du désabonnement:', err);
				const message = this.translate.instant('notification.unsubscription-error');
				this.toastService.show(message, "error");
			}
		});
	}

	async unsubscribe() {
		try {
			const token = await this.getCurrentToken();
			if (token) {
				this.deleteSubscription(token);
				await deleteToken(messaging);
			}

			try {
				const registration = await navigator.serviceWorker.ready;

				if (registration) {
					await registration.unregister();
				}
			} catch (pushError) {
				console.error('❌ Erreur suppression push subscription:', pushError);
			}
			return true;
		} catch (error) {
			console.error('❌ Erreur lors du désabonnement:', error);
			return false;
		}
	}

	async isSubscribed(): Promise<boolean> {
		try {
			const hasActiveServiceWorker = await this.hasActiveServiceWorker();
			if (!hasActiveServiceWorker) {
				return false;
			}

			const token = await this.getCurrentToken();
			if (!token) {
				return false;
			}

			const registration = await navigator.serviceWorker.ready;
			const subscription = await registration.pushManager.getSubscription();

			return subscription !== null;
		} catch (error) {
			return false;
		}
	}

	/**
	 * Vérifie si un Service Worker Firebase est actif
	 */
	async hasActiveServiceWorker(): Promise<boolean> {
		try {
			if (!('serviceWorker' in navigator)) {
				return false;
			}

			const registrations = await navigator.serviceWorker.getRegistrations();

			if (registrations.length === 0) {
				return false;
			}

			const firebaseSW = registrations.find(reg =>
				reg.active?.scriptURL.includes('firebase-messaging-sw.js')
			);

			if (!firebaseSW) {
				return false;
			}

			return firebaseSW.active?.state === 'activated';
		} catch (error) {
			console.error('Erreur vérification Service Worker:', error);
			return false;
		}
	}
}
